/**
 * @class ShopifyAPI
 * @description 提供了与 Shopify 购物车交互的静态方法。
 */
class ShopifyAPI {
  /**
   * 执行单个商品添加到购物车的 AJAX 请求。
   * @param {string} variantId - 变体 ID。
   * @param {number} quantity - 数量。
   * @param {string} productTitle - 产品标题。
   * @param {HTMLElement | null} button - 触发操作的按钮元素。
   * @returns {Promise<object>} - Shopify 返回的数据。
   */
  static executeAddToCart(variantId, quantity, productTitle, button = null) {
    if (button) {
      button.classList.add("loading");
      button.disabled = true;
    }

    const formData = new FormData();
    formData.append("id", variantId);
    formData.append("quantity", quantity);

    let sections = "cart-icon-bubble";
    const cartDrawer = document.querySelector("cart-drawer");
    if (cartDrawer) {
      sections += `,${cartDrawer
        .closest(".shopify-section")
        .id.replace("shopify-section-", "")}`;
    }
    formData.append("sections", sections);
    formData.append("sections_url", window.location.pathname);

    return fetch(window.theme?.routes?.cartAdd || "/cart/add.js", {
      method: "POST",
      headers: {
        Accept: "application/javascript",
        "X-Requested-With": "XMLHttpRequest",
      },
      body: formData,
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.status && data.status !== "success") {
          console.error("添加购物车失败:", data.description);
          this.handleButtonState(button, "error");
          alert(data.description || "添加购物车失败");
          return Promise.reject(data.description);
        }
        this.updateCartIcon(data);
        this.handleButtonState(button, "success");
        if (cartDrawer) {
          setTimeout(() => cartDrawer.renderContents(data, true), 700);
        }
        return data;
      })
      .catch((error) => {
        console.error("添加购物车请求失败:", error);
        this.handleButtonState(button, "error");
        alert("添加购物车请求失败，请稍后再试");
        return Promise.reject(error);
      });
  }

  /**
   * 执行批量商品添加到购物车的 AJAX 请求。
   * @param {Array<object>} products - 商品数组 [{ variantId, quantity }]。
   * @param {HTMLElement | null} button - 触发操作的按钮元素。
   * @returns {Promise<object>} - Shopify 返回的数据。
   */
  static bulkAddToCart(products, button = null) {
    if (!products || !products.length) return Promise.resolve();

    if (button) {
      button.classList.add("loading");
      button.disabled = true;
    }

    const items = products.map((p) => ({
      id: p.variantId,
      quantity: p.quantity,
    }));

    let sections = "cart-icon-bubble";
    const cartDrawer = document.querySelector("cart-drawer");
    if (cartDrawer) {
      sections += `,${cartDrawer
        .closest(".shopify-section")
        .id.replace("shopify-section-", "")}`;
    }

    if (items.length === 1) {
      // 单个商品使用FormData方式提交
      const formData = new FormData();
      formData.append("id", items[0].id);
      formData.append("quantity", items[0].quantity);
      formData.append("sections", sections);
      formData.append("sections_url", window.location.pathname);

      return fetch(window.theme?.routes?.cartAdd || "/cart/add.js", {
        method: "POST",
        headers: {
          Accept: "application/javascript",
          "X-Requested-With": "XMLHttpRequest",
        },
        body: formData,
      })
        .then((response) => response.json())
        .then((data) => {
          if (data.status && data.status !== "success") {
            console.error("批量添加购物车失败:", data.description);
            this.handleButtonState(button, "error");
            return Promise.reject(data.description);
          }
          this.updateCartIcon(data);
          if (cartDrawer) {
            setTimeout(() => cartDrawer.renderContents(data, true), 700);
          }
          this.handleButtonState(button, "success");
          return data;
        })
        .catch((error) => {
          console.error("批量添加购物车请求失败:", error);
          this.handleButtonState(button, "error");
          alert("批量添加购物车请求失败，请稍后再试");
          return Promise.reject(error);
        });
    } else {
      // 多个商品使用JSON格式提交
      return fetch("/cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          items: items,
          sections: sections,
          sections_url: window.location.pathname,
        }),
      })
        .then((response) => response.json())
        .then((data) => {
          if (data.status && data.status !== "success") {
            console.error("批量添加购物车失败:", data.description);
            this.handleButtonState(button, "error");
            return Promise.reject(data.description);
          }
          this.updateCartIcon(data);
          if (cartDrawer) {
            setTimeout(() => cartDrawer.renderContents(data, true), 700);
          }
          this.handleButtonState(button, "success");
          return data;
        })
        .catch((error) => {
          console.error("批量添加购物车请求失败:", error);
          this.handleButtonState(button, "error");
          alert("批量添加购物车请求失败，请稍后再试");
          return Promise.reject(error);
        });
    }
  }

  /**
   * 更新购物车图标。
   * @param {object} data - Shopify 返回的数据。
   */
  static updateCartIcon(data) {
    const cartIconBubble = document.getElementById("cart-icon-bubble");
    if (cartIconBubble && data.sections && data.sections["cart-icon-bubble"]) {
      cartIconBubble.innerHTML = data.sections["cart-icon-bubble"];
      return;
    }
    // Fallback
    const count = data.item_count || 0;
    document
      .querySelectorAll(".header__cart-count-number")
      .forEach((el) => (el.textContent = count));
    document
      .querySelectorAll(".header__cart-count .visually-hidden")
      .forEach(
        (el) => (el.textContent = `${count} item${count === 1 ? "" : "s"}`)
      );
    document
      .querySelectorAll(".header__cart-count")
      .forEach((el) => el.classList.toggle("hidden", count === 0));
  }

  /**
   * 处理按钮状态（加载、成功、错误）。
   * @param {HTMLElement | null} button - 按钮元素。
   * @param {'loading' | 'success' | 'error' | 'reset'} state - 状态。
   */
  static handleButtonState(button, state) {
    if (!button) return;

    button.classList.remove("loading", "success", "error");
    button.disabled = false;

    switch (state) {
      case "loading":
        button.classList.add("loading");
        button.disabled = true;
        break;
      case "success":
        button.classList.add("success");
        setTimeout(() => {
          button.classList.remove("success");
          button.disabled = false;
        }, 1000);
        break;
      case "error":
        button.classList.add("error");
        setTimeout(() => {
          button.classList.remove("error");
          button.disabled = false;
        }, 1000);
        break;
      case "reset":
      default:
        // Already reset
        break;
    }
  }
}

/**
 * @class ProductRecommender
 * @description 管理单个产品推荐区块的逻辑和状态。
 */
class ProductRecommender {
  /**
   * @param {HTMLElement} widgetElement - 产品推荐区块的根元素 (.rp-widget)。
   */
  constructor(widgetElement) {
    this.widget = widgetElement;
    this.blockId = this.widget.dataset.blockId;
    this.currency = this.widget.dataset.currency[0] || "";
    this.modal = document.getElementById(`rp-modal-${this.blockId}`);
    this.productWrappers = this.widget.querySelectorAll(".rp-product-flex");
    this.modalProductWrappers = this.modal
      ? this.modal.querySelectorAll(".rp-product-flex")
      : [];
    this.exploreBtn = this.widget.querySelector(".rp-explore-btn");
    this.closeBtn = this.modal
      ? this.modal.querySelector(".rp-modal__close")
      : null;
    this.modalAddBtn = this.modal
      ? this.modal.querySelector(".rp-modal__add-btn")
      : null;
    this.totalPriceEl = this.modal
      ? this.modal.querySelector(`#rp-total-price-${this.blockId} span`)
      : null;
    this.totalItemsEl = this.modal
      ? this.modal.querySelector(".rp-modal__total-items")
      : null;

    // 状态管理
    this.state = {
      selectedProducts: [], // [{ productId, variantId, quantity, price, title, variantTitle, variantColor }]
      totalPrice: 0.0,
      totalItems: 0,
    };

    this.init();
  }

  /**
   * 初始化，绑定所有事件监听器。
   */
  init() {
    // 绑定主页面和弹窗内的产品交互
    this.productWrappers.forEach((wrapper) =>
      this.bindProductFlexHandlers(wrapper)
    );
    this.modalProductWrappers.forEach((wrapper) =>
      this.bindProductFlexHandlers(wrapper)
    );

    // 绑定弹窗控制按钮
    if (this.exploreBtn) {
      this.exploreBtn.addEventListener("click", (e) => {
        e.preventDefault();
        this.openModal();
      });
    }
    if (this.closeBtn) {
      this.closeBtn.addEventListener("click", () => this.closeModal());
    }
    if (this.modal) {
      this.modal.addEventListener("click", (e) => {
        if (e.target === this.modal) this.closeModal();
      });
    }
    if (this.modalAddBtn) {
      this.modalAddBtn.addEventListener("click", () =>
        this.handleBulkAddToCart()
      );
    }

    // 绑定全局点击事件来关闭下拉菜单
    document.addEventListener("click", (e) => {
      if (!e.target.closest(".re-product-item__variants-dropdown")) {
        const activeDropdowns = document.querySelectorAll(
          ".re-product-item__variants-dropdown.active"
        );
        activeDropdowns.forEach((dropdown) =>
          dropdown.classList.remove("active")
        );
      }
    });
  }

  /**
   * 为产品容器绑定事件处理器 (使用事件委托)。
   * @param {HTMLElement} productWrapper - 产品容器元素 (.rp-product-flex)。
   */
  bindProductFlexHandlers(productWrapper) {
    if (
      !productWrapper ||
      productWrapper.classList.contains("re-handler-attached")
    )
      return;
    productWrapper.classList.add("re-handler-attached");

    // 使用事件委托处理所有点击事件
    productWrapper.addEventListener("click", (e) => {
      const target = e.target;

      // 处理变体下拉菜单的点击
      const variantDropdown = target.closest(
        ".re-product-item__variants-dropdown"
      );
      if (variantDropdown) {
        // 切换下拉菜单的显示状态
        if (
          target.closest(".re-product-item__variants-selected") ||
          target.closest(".re-product-item__variant-arrow")
        ) {
          e.stopPropagation();
          variantDropdown.classList.toggle("active");
          return;
        }

        // 处理选项点击
        if (target.classList.contains("re-product-item__variants-option") ||
          target.closest(".re-product-item__variants-option")) {
          e.stopPropagation();
          // 获取选项元素（可能是子元素被点击）
          const option = target.classList.contains("re-product-item__variants-option") ?
            target : target.closest(".re-product-item__variants-option");

          // 更新选中状态
          const options = variantDropdown.querySelectorAll(
            ".re-product-item__variants-option"
          );
          options.forEach((opt) => opt.classList.remove("selected"));
          option.classList.add("selected");

          // 关闭下拉菜单
          variantDropdown.classList.remove("active");

          // 触发变体变更处理
          this.handleVariantChangeCustom(variantDropdown, option);
          return;
        }
      }

      // 数量按钮
      if (target.classList.contains("re-quantity-btn")) {
        this.handleQuantityChange(target);
        return;
      }

      // 添加到购物车按钮
      if (target.classList.contains("re-btn--add-to-cart")) {
        this.handleAddToCartClick(target);
        return;
      }

      // "Explore" 模式下移除按钮
      if (target.classList.contains("rp-item-remove-selected") ||
        target.closest(".rp-item-remove-selected")) {
        const removeBtn = target.classList.contains("rp-item-remove-selected") ?
          target : target.closest(".rp-item-remove-selected");
        const variantId = removeBtn.dataset.variantId;
        const productItem = removeBtn.closest(".re-product-item");
        const productId = productItem?.querySelector(".re-btn--add-to-cart")
          ?.dataset.productId;
        if (variantId && productId) {
          this.removeProduct(productId, variantId);
        }
        return;
      }
    });
  }

  /**
   * 处理自定义变体选择变化。
   * @param {HTMLElement} dropdown - 变体下拉菜单元素。
   * @param {HTMLElement} selectedOption - 选中的选项元素。
   */
  handleVariantChangeCustom(dropdown, selectedOption) {
    const productItem = dropdown.closest(".re-product-item");
    if (!productItem) return;

    const addBtn = productItem.querySelector(".re-btn--add-to-cart");

    if (addBtn) {
      addBtn.setAttribute("data-product", selectedOption.dataset.value);
      addBtn.setAttribute("data-price", selectedOption.dataset.price || "");
      addBtn.setAttribute("data-compare-at-price", selectedOption.dataset.compareAtPrice || "");
    }

    // 更新变体标题显示
    const variantTitle = selectedOption.dataset.title || selectedOption.textContent.trim();
    const variantTitleEl = productItem.querySelector(
      ".re-product-item__variants-selected-text"
    );
    if (variantTitleEl) {
      variantTitleEl.textContent = variantTitle;
    }

    // 更新产品卡片信息
    this.updateProductCardInfo(productItem, selectedOption);

    // 处理颜色变体的圆球显示
    this.updateColorSwatch(productItem, selectedOption);
  }

  /**
   * 更新产品卡片信息
   * @param {HTMLElement} productItem - 产品项元素
   * @param {HTMLElement} selectedOption - 选中的选项元素
   */
  updateProductCardInfo(productItem, selectedOption) {
    const updateElement = (selector, attribute, prefix = "", isSrc = false, isCompare = false) => {
      const el = productItem.querySelector(selector);
      const value = selectedOption.dataset[attribute];
      if (el && value !== undefined) {
        if (isSrc) {
          el.src = value;
        } else if (isCompare) {
          const compareValue = parseFloat(value);
          el.textContent = compareValue > 0 ? `${prefix}${compareValue}` : "";
        } else {
          el.textContent = `${prefix}${value}`;
        }
      }
    };

    updateElement(".re-product-item__image", "image", "", true);
    updateElement(".re-product-item__price-current", "price", this.currency);
    updateElement(".re-product-item__price-old", "compareAtPrice", this.currency, false, true);
    updateElement(".re-product-item__description", "description");
  }

  /**
   * 更新颜色变体圆球显示
   * @param {HTMLElement} productItem - 产品项元素
   * @param {HTMLElement} selectedOption - 选中的选项元素
   */
  updateColorSwatch(productItem, selectedOption) {
    const colorSwatch = selectedOption.querySelector(".variant-color-swatch");
    const targetContainer = productItem.querySelector(
      ".re-product-item__variants-selected"
    );

    // 移除现有的色块
    const existingSwatch = targetContainer.querySelector(".variant-color-swatch");
    if (existingSwatch) {
      existingSwatch.remove();
    }

    // 如果有颜色色块，添加到选中区域
    if (colorSwatch) {
      const newSwatch = colorSwatch.cloneNode(true);
      targetContainer.insertBefore(newSwatch, targetContainer.firstChild);
    }
  }

  /**
   * 从变体标题中提取颜色代码
   * @param {string} variantTitle - 变体标题
   * @returns {string|null} - 颜色代码或null
   */
  extractColorFromTitle(variantTitle) {
    if (!variantTitle) return null;

    const titleParts = variantTitle.split('(');
    if (titleParts.length > 1) {
      const colorPart = titleParts[1].split(')')[0];
      // 检查是否是颜色代码格式
      if (colorPart.includes('#')) {
        return colorPart.trim();
      } else if (colorPart.length === 6 && /^[0-9A-Fa-f]{6}$/.test(colorPart)) {
        return `#${colorPart}`;
      }
    }
    return null;
  }

  /**
   * 处理数量变化。
   * @param {HTMLElement} btn - 数量按钮。
   */
  handleQuantityChange(btn) {
    const productItem = btn.closest(".re-product-item");
    const input = productItem?.querySelector('input[type="number"]');
    if (!input) return;

    let value = parseInt(input.value) || 1;
    const action = btn.dataset.action;

    if (action === "increment") value++;
    else if (action === "decrement" && value > 1) value--;

    input.value = value;
  }

  /**
   * 处理添加到购物车按钮点击。
   * @param {HTMLElement} btn - 添加按钮。
   */
  handleAddToCartClick(btn) {
    const productItem = btn.closest(".re-product-item");
    if (!productItem) return;

    const productId = btn.dataset.productId;
    const variantId = btn.dataset.product;
    const price = btn.dataset.price;
    const enableExplore = btn.dataset.enableExplore === "true";
    const quantityInput = productItem.querySelector(`#quantity-${productId}`);
    const quantity = parseInt(quantityInput?.value || "1");
    const productTitle =
      productItem.querySelector(".re-product-item__title h6")?.textContent || "";

    // 获取变体标题
    const variantTitleEl = productItem.querySelector(".re-product-item__variants-selected-text");
    const variantTitle = variantTitleEl ? variantTitleEl.textContent.trim() : productTitle;

    // 获取颜色色块
    const colorSwatch = productItem.querySelector(".re-product-item__variants-selected .variant-color-swatch");
    const variantColor = colorSwatch ? colorSwatch.style.backgroundColor : null;

    if (enableExplore && this.modal) {
      this.addProduct({
        productId,
        variantId,
        quantity,
        price,
        title: productTitle,
        variantTitle,
        variantColor,
        productItemElement: productItem,
      });
    } else {
      ShopifyAPI.executeAddToCart(variantId, quantity, productTitle, btn);
    }
  }

  /**
   * 添加商品到内部状态 (Explore 模式)。
   * @param {object} productData - 商品数据。
   */
  addProduct(productData) {
    const existingIndex = this.state.selectedProducts.findIndex(
      (p) =>
        p.variantId === productData.variantId &&
        p.productId === productData.productId
    );

    if (existingIndex >= 0) {
      this.state.selectedProducts[existingIndex].quantity +=
        productData.quantity;
    } else {
      // 添加新产品到选中列表
      this.state.selectedProducts.push({
        productId: productData.productId,
        variantId: productData.variantId,
        quantity: productData.quantity,
        price: productData.price,
        title: productData.title,
        variantTitle: productData.variantTitle,
        variantColor: productData.variantColor
      });
    }

    this.updateStateAndUI();
    this.updateSingleProductItemDisplay(productData.productItemElement);
  }

  /**
   * 从内部状态移除商品 (Explore 模式)。
   * @param {string} productId - 父产品 ID。
   * @param {string} variantId - 变体 ID。
   */
  removeProduct(productId, variantId) {
    const index = this.state.selectedProducts.findIndex(
      (p) => p.variantId === variantId && p.productId === productId
    );

    if (index !== -1) {
      this.state.selectedProducts.splice(index, 1);
      this.updateStateAndUI();
      const productItem = this.modal
        .querySelector(`.re-btn--add-to-cart[data-product-id="${productId}"]`)
        ?.closest(".re-product-item");
      if (productItem) {
        this.updateSingleProductItemDisplay(productItem);
      }
    }
  }

  /**
   * 更新总状态和全局 UI (总价, 总数)。
   */
  updateStateAndUI() {
    let total = 0;
    let itemsCount = 0;
    this.state.selectedProducts.forEach((p) => {
      total += parseFloat(p.price) * p.quantity;
      itemsCount += p.quantity;
    });

    this.state.totalPrice = total;
    this.state.totalItems = itemsCount;

    if (this.totalPriceEl) this.totalPriceEl.textContent = total.toFixed(2);
    if (this.totalItemsEl)
      this.totalItemsEl.textContent = `Total ${itemsCount} item(s) selected`;

    if (this.modalAddBtn) {
      if (this.state.selectedProducts.length > 0) {
        this.modalAddBtn.classList.add("rp-modal__add-btn-hover");
      } else {
        this.modalAddBtn.classList.remove("rp-modal__add-btn-hover");
      }
    }
  }

  /**
   * 更新单个产品项下已选变体的信息显示。
   * @param {HTMLElement} productItemElement - 产品项元素。
   */
  updateSingleProductItemDisplay(productItemElement) {
    if (!productItemElement) return;

    const productId = productItemElement.querySelector(".re-btn--add-to-cart")
      ?.dataset.productId;
    if (!productId) return;

    const variantsForThisItem = this.state.selectedProducts.filter(
      (p) => p.productId === productId
    );

    const infoElement = productItemElement.querySelector(
      ".re-product-item__info"
    );
    if (!infoElement) return;

    let container = infoElement.querySelector(
      ".rp-product-item-all-selected-variants"
    );
    if (!container) {
      container = document.createElement("div");
      container.className = "rp-product-item-all-selected-variants";
      infoElement.appendChild(container);
    }

    container.innerHTML = "";

    if (variantsForThisItem.length > 0) {
      const totalQty = variantsForThisItem.reduce(
        (acc, v) => acc + v.quantity,
        0
      );
      container.innerHTML = `<div class="rp-selected-items-summary">${totalQty} item(s) selected</div>`;

      // 显示已选变体信息，包括颜色色块
      variantsForThisItem.forEach((variant) => {
        const variantDiv = document.createElement("div");
        variantDiv.className = "rp-product-item-selected-info";

        // 构建颜色色块HTML
        const colorSwatchHTML = variant.variantColor ?
          `<span class="variant-color-swatch" style="background-color: ${variant.variantColor};"></span>` : '';

        variantDiv.innerHTML = `
          <span class="rp-selected-info-color">
            ${colorSwatchHTML}
          </span>
          <span class="rp-selected-info-title">
            ${variant.variantTitle}
          </span>
          <span class="rp-selected-info-quantity">Qty: ${variant.quantity}</span>
          <button class="rp-item-remove-selected" data-variant-id="${variant.variantId}">
            <svg class="trash-icon" viewBox="0 0 24 24" width="16" height="16">
              <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" fill="currentColor"/>
            </svg>
          </button>
        `;
        container.appendChild(variantDiv);
      });
    }
  }

  /**
   * 打开弹窗。
   */
  openModal() {
    if (this.modal) {
      this.modal.classList.add("modal-open");
      document.body.style.overflow = "hidden";
    }
  }

  /**
   * 关闭弹窗。
   */
  closeModal() {
    if (this.modal) {
      this.modal.classList.remove("modal-open");
      document.body.style.overflow = "";
    }
  }

  /**
   * 处理批量添加到购物车。
   */
  handleBulkAddToCart() {
    if (this.state.selectedProducts.length === 0) return;

    const products = this.state.selectedProducts.map((p) => ({
      variantId: p.variantId,
      quantity: p.quantity,
    }));

    ShopifyAPI.bulkAddToCart(products, this.modalAddBtn).then(() => {
      this.closeModal();
      this.state.selectedProducts = [];
      this.updateStateAndUI();

      // 清理所有产品项的显示
      const allProductItems = document.querySelectorAll(".re-product-item");
      allProductItems.forEach((item) => {
        this.updateSingleProductItemDisplay(item);
      });
    });
  }
}

/**
 * 初始化所有产品推荐区块
 */
document.addEventListener("DOMContentLoaded", function () {
  const widgets = document.querySelectorAll(".rp-widget");
  widgets.forEach((widget) => {
    new ProductRecommender(widget);
  });
});